import { Color } from '@tmh-website/shared/colors';

export type InfoTextButtonThemeColor =
  | Color.Blue_600
  | Color.Blue_900
  | Color.Blue_1000_Deep
  | Color.Green_900
  | Color.Neutral_100
  | Color.Neutral_200_OffWhite
  | Color.Neutral_300
  | Color.Neutral_400
  | Color.Neutral_500
  | Color.Neutral_600
  | Color.Neutral_900
  | Color.Neutral_950_TechDarkGrey
  | Color.Neutral_1000_Black
  | Color.Neutral_0_White;

export interface InfoTextButtonTheme {
  background: {
    normal: Color;
    hover: Color;
  };
  text: Color;
  tooltipThemeColor: InfoTextButtonThemeColor;
}

export interface InfoTextButtonTooltipTheme {
  background: Color;
  text: Color;
}

export const infoTextButtonThemes: { [key in InfoTextButtonThemeColor]: InfoTextButtonTheme } = {
  [Color.Blue_1000_Deep]: {
    background: {
      normal: Color.Blue_1000_Deep,
      hover: Color.Blue_900,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Blue_900,
  },
  [Color.Blue_600]: {
    background: {
      normal: Color.Blue_600,
      hover: Color.Blue_700,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Blue_900,
  },
  [Color.Blue_900]: {
    background: {
      normal: Color.Blue_900,
      hover: Color.Blue_800,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Blue_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme, based on the TMH styleguide.
   *
   * @see https://mobilityhouse.atlassian.net/browse/WEBSITE23-374
   */
  [Color.Green_900]: {
    background: {
      normal: Color.Green_900,
      hover: Color.Green_800,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Green_900,
  },

  [Color.Neutral_0_White]: {
    background: {
      normal: Color.Neutral_0_White,
      hover: Color.Neutral_100,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_200_OffWhite,
  },
  [Color.Neutral_100]: {
    background: {
      normal: Color.Neutral_100,
      hover: Color.Neutral_200_OffWhite,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_0_White,
  },
  [Color.Neutral_200_OffWhite]: {
    background: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_300,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_0_White,
  },
  [Color.Neutral_300]: {
    background: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_0_White,
  },
  [Color.Neutral_400]: {
    background: {
      normal: Color.Neutral_400,
      hover: Color.Neutral_500,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_0_White,
  },
  [Color.Neutral_500]: {
    background: {
      normal: Color.Neutral_500,
      hover: Color.Neutral_600,
    },
    text: Color.Blue_1000_Deep,
    tooltipThemeColor: Color.Neutral_0_White,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_600]: {
    background: {
      normal: Color.Neutral_600,
      hover: Color.Neutral_500,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Neutral_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_900]: {
    background: {
      normal: Color.Neutral_900,
      hover: Color.Neutral_800,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Neutral_1000_Black,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_950_TechDarkGrey]: {
    background: {
      normal: Color.Neutral_950_TechDarkGrey,
      hover: Color.Neutral_900,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Neutral_1000_Black,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_1000_Black]: {
    background: {
      normal: Color.Neutral_1000_Black,
      hover: Color.Neutral_900,
    },
    text: Color.Neutral_0_White,
    tooltipThemeColor: Color.Neutral_950_TechDarkGrey,
  },
};

export const infoTextButtonTooltipThemes: { [key in InfoTextButtonThemeColor]: InfoTextButtonTooltipTheme } = {
  [Color.Blue_1000_Deep]: {
    text: Color.Neutral_0_White,
    background: Color.Blue_1000_Deep,
  },
  [Color.Blue_600]: {
    text: Color.Neutral_0_White,
    background: Color.Blue_600,
  },
  [Color.Blue_900]: {
    text: Color.Neutral_0_White,
    background: Color.Blue_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme, based on the TMH styleguide.
   *
   * @see https://mobilityhouse.atlassian.net/browse/WEBSITE23-374
   */
  [Color.Green_900]: {
    text: Color.Neutral_0_White,
    background: Color.Green_900,
  },

  [Color.Neutral_0_White]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_0_White,
  },
  [Color.Neutral_100]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_100,
  },
  [Color.Neutral_200_OffWhite]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_200_OffWhite,
  },
  [Color.Neutral_300]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_300,
  },
  [Color.Neutral_400]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_400,
  },
  [Color.Neutral_500]: {
    text: Color.Blue_1000_Deep,
    background: Color.Neutral_500,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_600]: {
    text: Color.Neutral_0_White,
    background: Color.Neutral_600,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_900]: {
    text: Color.Neutral_0_White,
    background: Color.Neutral_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_950_TechDarkGrey]: {
    text: Color.Neutral_0_White,
    background: Color.Neutral_950_TechDarkGrey,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_1000_Black]: {
    text: Color.Neutral_0_White,
    background: Color.Neutral_1000_Black,
  },
};
